/**
 * Planner Ads / LSA customer IDs and shared-account campaign filter.
 * Same tables as Planner Pacing Settings. Opens as a modal, like Planner.
 * Saving the Ads CID copies it onto tenant_settings so apply does not
 * skip on CID drift.
 */
import { useEffect, useState } from 'react';
import { Loader2, Settings } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import type {
  BudgetPacingCampaignScope,
  GoogleBudgetSplit,
  PacingAccountScope,
} from '@/services/googleAdsMonthlyService';

export function parseCampaignIdList(raw: string): string[] {
  return raw
    .split(/[\s,]+/)
    .map((part) => part.replace(/\D/g, ''))
    .filter(Boolean);
}

function formatDollars(value: number): string {
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

function formatCustomerId(raw: string | null | undefined): string {
  const digits = (raw ?? '').replace(/\D/g, '');
  if (digits.length !== 10) return raw ?? '';
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function BudgetPacingAccountSettings({
  tenantCustomerId,
  scope,
  scopeLoading,
  mapped,
  unmapped,
  canEdit,
  saving,
  onSave,
  snapshotScope,
  split = null,
  splitLoading = false,
  openSignal = 0,
}: {
  tenantCustomerId: string;
  scope: PacingAccountScope | null;
  scopeLoading: boolean;
  mapped: boolean;
  unmapped: boolean;
  canEdit: boolean;
  saving: boolean;
  onSave?: (next: {
    adsCustomerId: string;
    lsaCustomerId: string;
    campaignNameFilter: string;
    includedCampaignIds: string[] | null;
    /** null = no decision made here; the backend leaves the switch as it is. */
    lsaAutopace: boolean | null;
    /** Display only; null = no decision made here. */
    combinedTotals: boolean | null;
    /** New Google LSA monthly line; null = unchanged. Ads takes the difference. */
    lsaMonthlyDollars: number | null;
  }) => void | Promise<void>;
  snapshotScope?: BudgetPacingCampaignScope | null;
  /** The two Google monthly lines in Planner; null hides the split section. */
  split?: GoogleBudgetSplit | null;
  splitLoading?: boolean;
  /** Bumped by the parent to open the dialog from elsewhere (the pacing card's "Change split"). */
  openSignal?: number;
}) {
  const [open, setOpen] = useState(false);
  const [ads, setAds] = useState('');
  const [lsa, setLsa] = useState('');
  // "Separate account for LSA" is the fact; the CID input lives behind it.
  const [separateLsa, setSeparateLsa] = useState(false);
  const [autopace, setAutopace] = useState(false);
  // An unknown read (env behind on migrations, DB hiccup) must not be saved
  // back as "off": that would silently unenroll the client. Only a known read
  // or an actual click produces a value; otherwise null leaves it alone.
  const [autopaceTouched, setAutopaceTouched] = useState(false);
  const autopaceKnown = typeof scope?.lsa_autopace === 'boolean';
  const [combined, setCombined] = useState(false);
  const [combinedTouched, setCombinedTouched] = useState(false);
  const combinedKnown = typeof scope?.combined_totals === 'boolean';
  const [nameFilter, setNameFilter] = useState('');
  const [restrictIds, setRestrictIds] = useState(false);
  const [idText, setIdText] = useState('');
  // Google LSA monthly line as typed; null means untouched, which shows the
  // Planner value and leaves the split alone on save. Reset when the dialog
  // closes, so no effect is needed to reseed it.
  const [lsaMonthlyDraft, setLsaMonthlyDraft] = useState<string | null>(null);

  useEffect(() => {
    if (!scope || !open) return;
    setAds(formatCustomerId(scope.ads_customer_id ?? scope.tenant_customer_id));
    // Seed from Planner OR the workspace mirror. If Planner lost the account
    // out-of-band while Core still mirrors it, the switch must open ON and the
    // input must show the mirrored account; otherwise any unrelated save would
    // blank the mirror and unenroll the client with no warning (Viktor, #2489).
    const plannerLsa = (scope.lsa_customer_id ?? '').replace(/\D/g, '');
    const mirrorLsa = (scope.tenant_lsa_customer_id ?? '').replace(/\D/g, '');
    setLsa(formatCustomerId(plannerLsa || mirrorLsa));
    setSeparateLsa(Boolean(plannerLsa || mirrorLsa));
    setAutopace(Boolean(scope.lsa_autopace));
    setAutopaceTouched(false);
    setCombined(Boolean(scope.combined_totals));
    setCombinedTouched(false);
    setNameFilter(scope.campaign_name_filter ?? '');
    const ids = scope.included_campaign_ids;
    setRestrictIds(ids !== null && ids !== undefined);
    setIdText(ids?.join('\n') ?? '');
  }, [scope, open]);

  const setDialogOpen = (next: boolean) => {
    if (!next) setLsaMonthlyDraft(null);
    setOpen(next);
  };

  // Programmatic open: each bump past zero opens; closing stays with the
  // dialog. State adjusted during render on a prop change, not in an effect.
  const [seenOpenSignal, setSeenOpenSignal] = useState(openSignal);
  if (openSignal !== seenOpenSignal) {
    setSeenOpenSignal(openSignal);
    if (openSignal > 0) setOpen(true);
  }

  const caseSensitive =
    nameFilter.trim() !== '' && nameFilter !== nameFilter.toLowerCase();
  const lsaDigits = lsa.replace(/\D/g, '');
  const lsaLinked = separateLsa && lsaDigits.length === 10;
  const lsaMirrored =
    Boolean(scope?.tenant_lsa_customer_id) &&
    (scope?.tenant_lsa_customer_id ?? '').replace(/\D/g, '') === lsaDigits;
  const plannerLsaDigits = (scope?.lsa_customer_id ?? '').replace(/\D/g, '');
  const mirrorLsaDigits = (scope?.tenant_lsa_customer_id ?? '').replace(/\D/g, '');
  const hadLsa = Boolean(plannerLsaDigits || mirrorLsaDigits);
  // Switching off with an account linked is an unlink in Planner, which also
  // drops the LSA reservation from the Ads target. Say so before Save.
  const unlinking = hadLsa && !separateLsa;
  // Same drift check the Ads CID has: the writer trusts the mirror, the
  // reservation trusts Planner, and they must agree.
  const lsaDrift =
    Boolean(mirrorLsaDigits) && plannerLsaDigits !== mirrorLsaDigits;
  const kept = snapshotScope?.kept;
  const dropped = snapshotScope?.dropped;
  const tenantDigits = tenantCustomerId.replace(/\D/g, '');
  const plannerAdsDigits = (scope?.ads_customer_id ?? '').replace(/\D/g, '');
  const cidDrift =
    Boolean(tenantDigits && plannerAdsDigits && tenantDigits !== plannerAdsDigits);

  // Monthly budget split: only when Planner has a Google Ads line to take the
  // difference from, and there is an LSA line or account to move it to.
  const splitVisible = Boolean(
    split && split.has_ads_line && (split.has_lsa_line || lsaLinked),
  );
  const splitTotal = split?.total_monthly_dollars ?? 0;
  const seededLsaMonthly = split?.google_lsa_monthly_dollars ?? 0;
  const lsaMonthlyTouched = lsaMonthlyDraft !== null;
  const lsaMonthly = lsaMonthlyDraft ?? String(seededLsaMonthly);
  const lsaMonthlyValue = lsaMonthly.trim() === '' ? NaN : Number(lsaMonthly);
  const lsaMonthlyError = !splitVisible || !lsaMonthlyTouched
    ? null
    : !Number.isFinite(lsaMonthlyValue) || lsaMonthlyValue <= 0
      ? 'Enter an amount above zero.'
      : lsaMonthlyValue >= splitTotal
        ? 'LSA must stay below the total; Google Ads cannot go to zero.'
        : null;
  const adsMonthlyDerived =
    splitTotal - (Number.isFinite(lsaMonthlyValue) ? lsaMonthlyValue : seededLsaMonthly);
  const lsaMonthlyChanged =
    splitVisible &&
    lsaMonthlyTouched &&
    lsaMonthlyError === null &&
    lsaMonthlyValue !== seededLsaMonthly;
  const cycleFollows = Boolean(
    split?.cycle_override_platforms?.some(
      (platform) => platform === 'Google Ads' || platform === 'Google LSA',
    ),
  );

  const save = async () => {
    const adsDigits = ads.replace(/\D/g, '');
    if (adsDigits.length !== 10) {
      toast.error('Google Ads customer ID must be 10 digits.');
      return;
    }
    try {
      await onSave?.({
        adsCustomerId: ads,
        lsaCustomerId: separateLsa ? lsa : '',
        campaignNameFilter: nameFilter,
        includedCampaignIds: restrictIds ? parseCampaignIdList(idText) : null,
        lsaAutopace: !lsaLinked
          ? false
          : autopaceTouched || autopaceKnown
            ? autopace
            : null,
        combinedTotals: combinedTouched || combinedKnown ? combined : null,
        lsaMonthlyDollars: lsaMonthlyChanged ? lsaMonthlyValue : null,
      });
      setDialogOpen(false);
    } catch {
      // Parent mutation already toasts.
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setDialogOpen(true)}
      >
        {scopeLoading ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Settings className="mr-2 h-4 w-4" />
        )}
        Account settings
      </Button>

      <Dialog open={open} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-2">
              Ads account settings
              {mapped ? (
                <Badge variant="outline">Same fields as Planner</Badge>
              ) : null}
            </DialogTitle>
            <DialogDescription>
              Google Ads account, an optional separate account for LSA, and the
              shared-account campaign filter. Same tables as Planner Pacing Settings.
            </DialogDescription>
          </DialogHeader>

          {scopeLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading Planner account links…
            </div>
          ) : unmapped ? (
            <p className="text-sm text-muted-foreground">
              Map this tenant to a Planner client to edit the Google Ads account,
              the LSA account, and the campaign name filter here. Until then, Core
              uses the workspace Ads account
              {tenantCustomerId ? ` (${formatCustomerId(tenantCustomerId)})` : ''}.
            </p>
          ) : mapped && scope ? (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Writes the Planner link tables. Saving the Ads CID resolves it
                under the MCC first (reachable client Ads account, not a manager
                or LSA-only CID), then updates this workspace so Monday/Thursday
                writes that account. Core cannot change Google LSA spend.
              </p>
              {scope.campaign_scope_error ? (
                <p className="text-sm text-destructive">
                  Campaign ID whitelist is unreadable in Planner (
                  {scope.campaign_scope_error}). Fix the list before the next apply.
                </p>
              ) : null}
              {cidDrift ? (
                <p className="text-sm text-destructive">
                  Workspace Ads CID {formatCustomerId(tenantCustomerId)} does not
                  match Planner {formatCustomerId(scope.ads_customer_id)}. Save to
                  sync them.
                </p>
              ) : null}
              {lsaDrift ? (
                <p className="text-sm text-destructive">
                  {plannerLsaDigits
                    ? `Planner LSA account ${formatCustomerId(plannerLsaDigits)} does not match this workspace ${formatCustomerId(mirrorLsaDigits)}. Save to sync them.`
                    : `Planner has no LSA account for this client, but this workspace records ${formatCustomerId(mirrorLsaDigits)} and Core is still pacing it. Save to restore the link in Planner, or switch LSA off to unlink on purpose.`}
                </p>
              ) : null}
              {typeof kept === 'number' && typeof dropped === 'number' && dropped > 0 ? (
                <p className="text-xs text-muted-foreground">
                  This snapshot is pacing {kept} campaign{kept === 1 ? '' : 's'} and
                  ignoring {dropped} others on the account.
                </p>
              ) : null}

              <div className="space-y-1.5">
                <Label htmlFor="pacing-ads-cid">Google Ads Account</Label>
                <Input
                  id="pacing-ads-cid"
                  value={ads}
                  onChange={(event) => setAds(event.target.value)}
                  placeholder="123-456-7890"
                  disabled={!canEdit}
                  autoComplete="off"
                />
              </div>

              <div className="rounded-md border border-border bg-warm-bg px-3 py-2 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="pacing-separate-lsa" className="cursor-pointer">
                    Separate account for LSA
                  </Label>
                  <Switch
                    id="pacing-separate-lsa"
                    aria-label="Separate account for LSA"
                    checked={separateLsa}
                    onCheckedChange={(next) => setSeparateLsa(Boolean(next))}
                    disabled={!canEdit}
                  />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {separateLsa
                    ? 'This client runs Local Services Ads on its own Google account. Core reserves its leftover before pacing Ads.'
                    : unlinking
                      ? 'Turning this off unlinks the LSA account in Planner; the Ads target stops reserving for LSA and rises by that amount, and Core stops setting this client\'s LSA budget. Re-enrolling takes the account plus the tick box.'
                      : 'Off when this client has no Local Services account, or runs LSA inside the Ads account above.'}
                </p>

                {separateLsa ? (
                  <div className="mt-3 space-y-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="pacing-lsa-cid">Account for LSA</Label>
                      <Input
                        id="pacing-lsa-cid"
                        value={lsa}
                        onChange={(event) => setLsa(event.target.value)}
                        placeholder="123-456-7890"
                        disabled={!canEdit}
                        autoComplete="off"
                      />
                      <p className="text-xs text-muted-foreground">
                        {lsaLinked
                          ? lsaMirrored
                            ? `Linked: CID ${formatCustomerId(lsaDigits)}, recorded on this workspace.`
                            : `Linked: CID ${formatCustomerId(lsaDigits)}. Save to resolve it under the MCC and record it on this workspace.`
                          : 'A 10-digit Google Ads customer ID. Save checks it is a Local Services account under the MCC.'}
                      </p>
                    </div>

                    <label className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        className="mt-1"
                        aria-label="Let Core set this client's LSA budget"
                        checked={lsaLinked && autopace}
                        disabled={!canEdit || !lsaLinked}
                        onChange={(event) => {
                          setAutopaceTouched(true);
                          setAutopace(event.target.checked);
                        }}
                      />
                      <span>
                        Let Core set this client&apos;s LSA budget
                        <span className="block text-xs text-muted-foreground">
                          {!lsaLinked
                            ? 'Enter the account first. Without one there is nothing for Core to manage.'
                            : !autopaceKnown && !autopaceTouched
                              ? 'Current state unknown on this workspace; saving leaves it unchanged unless you tick or untick it.'
                              : 'On: the Monday/Thursday tick sets the LSA daily budget from live leftover, floor $1/week, audited. Off: Core only shows the weekly rec.'}
                        </span>
                      </span>
                    </label>
                  </div>
                ) : null}
              </div>

              {separateLsa ? (
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1"
                    aria-label="Combine Google Ads and LSA in the daily totals"
                    checked={combined}
                    disabled={!canEdit}
                    onChange={(event) => {
                      setCombinedTouched(true);
                      setCombined(event.target.checked);
                    }}
                  />
                  <span>
                    Combine Google Ads and LSA in the daily totals
                    <span className="block text-xs text-muted-foreground">
                      Display only. Current daily and Target daily on the pacing card add the LSA account in. Budgets stay two Planner lines and the writers are unchanged.
                      {!combinedKnown && !combinedTouched
                        ? ' Not yet recorded for this workspace; saving leaves it as it is unless you tick or untick it.'
                        : ''}
                    </span>
                  </span>
                </label>
              ) : null}

              {splitVisible && split ? (
                <div className="space-y-2 rounded-md border border-border bg-warm-bg px-3 py-2 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-medium">Monthly budget split</span>
                    {splitLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    ) : null}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Total {formatDollars(splitTotal)} a month stays the same. Changing LSA
                    moves the difference onto Google Ads.
                  </p>
                  <div className="space-y-1.5">
                    <Label htmlFor="pacing-lsa-monthly">Google LSA, monthly</Label>
                    <Input
                      id="pacing-lsa-monthly"
                      type="number"
                      inputMode="decimal"
                      min={1}
                      step={1}
                      value={lsaMonthly}
                      onChange={(event) => setLsaMonthlyDraft(event.target.value)}
                      disabled={!canEdit || splitLoading}
                      autoComplete="off"
                    />
                    {lsaMonthlyError ? (
                      <p className="text-xs text-destructive">{lsaMonthlyError}</p>
                    ) : null}
                  </div>
                  <p className="tabular-nums">
                    Google Ads, monthly: {formatDollars(adsMonthlyDerived)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {cycleFollows
                      ? 'This cycle follows at once.'
                      : 'Applies to the current cycle where Planner has a budget row for it.'}
                  </p>
                </div>
              ) : null}

              <div className="space-y-1.5">
                <Label htmlFor="pacing-name-filter">Only campaigns named</Label>
                <Input
                  id="pacing-name-filter"
                  value={nameFilter}
                  onChange={(event) => setNameFilter(event.target.value)}
                  placeholder="e.g. a client name or campaign prefix; blank includes every campaign"
                  disabled={!canEdit}
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground">
                  {nameFilter.trim()
                    ? caseSensitive
                      ? 'Has a capital, so the match is exact. CO will not match Wisconsin.'
                      : 'All lowercase, so capitalisation is ignored.'
                    : 'Leave blank unless this client shares its Google Ads account. A capital makes the match exact.'}
                </p>
              </div>

              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={restrictIds}
                  disabled={!canEdit}
                  onChange={(event) => setRestrictIds(event.target.checked)}
                />
                <span>
                  Restrict to these campaign IDs
                  <span className="block text-xs text-muted-foreground">
                    Whitelist wins over the name filter, including an empty list
                    (pace nothing).
                  </span>
                </span>
              </label>
              {restrictIds ? (
                <Textarea
                  aria-label="Included campaign IDs"
                  value={idText}
                  onChange={(event) => setIdText(event.target.value)}
                  placeholder="One campaign ID per line"
                  disabled={!canEdit}
                  className="min-h-[72px]"
                />
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Planner account settings are not available on this backend.
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            {canEdit && mapped && scope ? (
              <Button
                type="button"
                className="bg-coral hover:bg-coral-hover"
                disabled={
                  saving ||
                  ads.replace(/\D/g, '').length !== 10 ||
                  (separateLsa && lsaDigits.length !== 10) ||
                  lsaMonthlyError !== null
                }
                onClick={() => void save()}
              >
                {saving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Save account settings
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function formatLinkedCustomerId(raw: string | null | undefined): string {
  return formatCustomerId(raw);
}
